import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';


export function useUrlFilters(
  keys: readonly string[],
  defaults: Record<string, string> = {},
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const getFilter = useCallback(
    (key: string): string => searchParams.get(key) ?? (defaults[key] ?? ''),
    [searchParams, defaults],
  );

  const setFilter = useCallback(
    (key: string, value: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          const defaultVal = defaults[key] ?? '';
          if (value && value !== defaultVal) {
            params.set(key, value);
          } else {
            params.delete(key);
          }
          params.delete('page');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, defaults],
  );

  const setFilters = useCallback(
    (updates: Record<string, string>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            const defaultVal = defaults[key] ?? '';
            if (value && value !== defaultVal) {
              params.set(key, value);
            } else {
              params.delete(key);
            }
          });
          params.delete('page');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, defaults],
  );

  const setFiltersAndPage = useCallback(
    (updates: Record<string, string>, pageNum: number) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            const defaultVal = defaults[key] ?? '';
            if (value && value !== defaultVal) {
              params.set(key, value);
            } else {
              params.delete(key);
            }
          });
          if (pageNum > 0) {
            params.set('page', String(pageNum + 1));
          } else {
            params.delete('page');
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, defaults],
  );

  const page = useMemo(
    () => Math.max(0, parseInt(searchParams.get('page') ?? '1', 10) - 1),
    [searchParams],
  );

  const setPage = useCallback(
    (p: number) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (p > 0) {
            params.set('page', String(p + 1));
          } else {
            params.delete('page');
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetAll = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const queryString = useMemo(
    () =>
      keys
        .map((key) => {
          const v = searchParams.get(key);
          return v != null && v !== ''
            ? `${key}=${encodeURIComponent(v.trim())}`
            : null;
        })
        .filter(Boolean)
        .join('&'),
    [keys, searchParams],
  );

  return {
    getFilter,
    setFilter,
    setFilters,
    setFiltersAndPage,
    page,
    setPage,
    resetAll,
    queryString,
    searchParams,
  };
}
